/**
 * What the staff app shows for a path it has no screen for, and for a screen
 * that throws.
 *
 * **Before this, both were React Router's own default** — an unstyled page
 * reading *"404 Not Found"* (or *"Unexpected Application Error"*), with no
 * navigation and no way back but the browser's. The router had no `*` route
 * and no `errorElement`, and the server answers every unknown path with the
 * SPA's `index.html` and a **200** (`server/handler.ts`), so the router was
 * the only thing that could ever say "not found" — and it said it badly.
 *
 * What reached it, found by walking the product rather than the code: every
 * **Client login** and **Register** link on the template gallery's demos
 * (`/accounts/…`, which only a firm's own domain routes), the demo hero's
 * `/contact`, an old bookmark, a mistyped address. The demo links are fixed at
 * their source (`site/render.tsx`, `site/blocks`); this is the floor under
 * everything else.
 *
 * `NotFound` is a **child of the shell**, so the sidebar and the bar are still
 * there — a person who lands on a dead link is one click from anywhere, not
 * stranded. `RouteError` replaces the shell, because it is what renders when
 * the shell's own subtree threw, and it cannot assume any of it works.
 *
 * **Wave 15F** (C14): drawn on the kit — `PageHeader` for the title, the
 * kit's buttons for the way out — in one `StatusPanel` that the paywall and the
 * sign-in callback share, so the app's three "you cannot go on from here"
 * screens look like one family.
 */

import { Button, PageHeader, buttonClass } from '@fiveox/ui'
import { FileQuestion, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { isRouteErrorResponse, Link, useLocation, useRouteError } from 'react-router'

/** A centred card: an icon tile, the kit's page title, what happened, and the
 *  way on. Shared by `NotFound`, `RouteError`, `Paywall` and `Callback`. */
export function StatusPanel({
  icon,
  title,
  description,
  children,
  actions,
  fullScreen = false,
}: {
  icon: ReactNode
  title: string
  description?: ReactNode
  /** Anything under the description — a code, a switcher, a reference. */
  children?: ReactNode
  /** The way on: one primary button, at most one secondary. */
  actions?: ReactNode
  /** Outside the shell: fill the viewport on the page background. */
  fullScreen?: boolean
}) {
  return (
    <div
      className={`bg-background flex items-center justify-center px-4 py-12 ${
        fullScreen ? 'min-h-screen' : 'min-h-[60vh]'
      }`}
    >
      <div
        data-status-panel=""
        className="border-border bg-card w-full max-w-md space-y-5 rounded-2xl border p-6 shadow-sm sm:p-8"
      >
        <span className="bg-muted text-muted-foreground flex h-11 w-11 items-center justify-center rounded-xl">
          {icon}
        </span>
        <PageHeader title={title} description={description} />
        {children && <div className="text-muted-foreground space-y-2 text-sm leading-relaxed">{children}</div>}
        {actions && <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap">{actions}</div>}
      </div>
    </div>
  )
}

/** The `*` route. */
export function NotFound() {
  const { pathname } = useLocation()
  return (
    <StatusPanel
      icon={<FileQuestion size={22} aria-hidden />}
      title="There is no page here"
      description="The link may be old, or the address mistyped."
      actions={
        <Link to="/" className={buttonClass()}>
          Go to the overview
        </Link>
      }
    >
      <p>
        <code className="bg-muted text-foreground rounded px-1.5 py-0.5 text-xs break-all">{pathname}</code>{' '}
        is not a page in ProDesk.
      </p>
      <p>Everything else is where it was — use the menu, or start from the overview.</p>
    </StatusPanel>
  )
}

/**
 * The root route's `errorElement`. A thrown `404` response is the same answer
 * as `NotFound`; anything else is a fault, said plainly, with the one action
 * that fixes a transient one.
 */
export function RouteError() {
  const error = useRouteError()
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <div className="bg-background min-h-screen">
        <NotFound />
      </div>
    )
  }
  return (
    <StatusPanel
      fullScreen
      icon={<TriangleAlert size={22} aria-hidden />}
      title="This screen could not be shown"
      description="Something went wrong while it was loading."
      actions={
        <>
          <a href="/" className={buttonClass({ variant: 'outline' })}>
            Go to the overview
          </a>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </>
      }
    >
      <p>Reloading usually brings it back; if it does not, the overview is a safe place to start.</p>
    </StatusPanel>
  )
}
